import React, { useState, useEffect } from 'react';
import { Monitor, Smartphone, Tablet, MapPin, Clock, AlertTriangle, X } from 'lucide-react';
import { Button } from './ui/Button';
import { useAuth } from '../contexts/AuthContext';
import sessionManagementService from '../services/sessionManagementService';
import LoadingState from './ui/LoadingState';

export default function ActiveSessions() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  useEffect(() => {
    if (user) {
      loadSessionData();
    }
  }, [user]);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      const [sessionsData, alertsData] = await Promise.all([
        sessionManagementService.getActiveSessions(user.id),
        sessionManagementService.getAccountAlerts(user.id)
      ]);
      setSessions(sessionsData);
      setAlerts(alertsData);
    } catch (error) {
      console.error('Error loading session data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      setRevoking(sessionId);
      await sessionManagementService.revokeSession(sessionId);
      await loadSessionData();
    } catch (error) {
      console.error('Error revoking session:', error);
    } finally {
      setRevoking(null);
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile': return Smartphone;
      case 'tablet': return Tablet;
      default: return Monitor;
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const formatAlertType = (type) => {
    const types = {
      'concurrent_sessions': 'Multiple Concurrent Sessions',
      'location_jump': 'Impossible Travel Detected',
      'device_limit': 'Too Many Locations',
      'rapid_ip_change': 'Rapid IP Changes'
    };
    return types[type] || type;
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  if (loading) {
    return <LoadingState message="Loading active sessions..." />;
  }

  return (
    <div className="space-y-6">
      {/* Security Alerts */}
      {alerts.length > 0 && (
        <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-semibold text-foreground">Security Alerts</h3>
          </div>
          
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={`p-4 rounded-lg border ${getAlertColor(alert.severity)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium mb-1">{formatAlertType(alert.type)}</p>
                    <p className="text-sm opacity-75">
                      Detected {formatTimeAgo(alert.createdAt)}
                    </p>
                    {alert.details && (
                      <p className="text-xs mt-2 opacity-60">
                        {JSON.stringify(alert.details)}
                      </p>
                    )}
                  </div>
                  <span className="text-xs font-medium uppercase px-2 py-1 rounded">
                    {alert.severity}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-sm text-muted-foreground mt-4">
            ⚠️ If you see suspicious activity, revoke unknown sessions below and change your password immediately.
          </p>
        </div>
      )}

      {/* Active Sessions */}
      <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">Active Sessions</h3>
        
        <div className="space-y-3">
          {sessions.map((session) => {
            const DeviceIcon = getDeviceIcon(session.deviceType);
            
            return (
              <div
                key={session.id}
                className={`p-4 rounded-lg border ${
                  session.isCurrent
                    ? 'border-primary-200 bg-primary-50'
                    : 'border-border bg-muted/30'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <DeviceIcon className="w-5 h-5 text-primary-600" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-foreground">{session.deviceName}</p>
                      {session.isCurrent && (
                        <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2 py-0.5 rounded">
                          Current
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{session.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Last active {formatTimeAgo(session.lastActivity)}</span>
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-1">
                      IP: {session.ipAddress}
                    </p>
                  </div>

                  {!session.isCurrent && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRevokeSession(session.id)}
                      disabled={revoking === session.id}
                      className="flex-shrink-0"
                    >
                      <X className="w-4 h-4 mr-1" />
                      {revoking === session.id ? 'Revoking...' : 'Revoke'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Security Tip:</strong> Only keep sessions active on devices you recognize. 
            Revoke any unfamiliar sessions immediately.
          </p>
        </div>
      </div>

      {/* Session Limits Info */}
      <div className="bg-card rounded-2xl border border-border/50 p-6 shadow-sm">
        <h4 className="font-semibold text-foreground mb-2">Session Limits</h4>
        <p className="text-sm text-muted-foreground mb-3">
          Your account is limited to <strong>2 active sessions</strong> at a time to prevent unauthorized sharing.
        </p>
        <p className="text-xs text-muted-foreground">
          Current active sessions: <strong>{sessions.length} / 2</strong>
        </p>
      </div>
    </div>
  );
}
